import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, StatusBadge, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { ClipboardCheck, Plus, Sparkles, Trash2, Check, Pencil, ListChecks, CalendarDays, User } from "lucide-react";

const TYPE_MAP = {
  foretaksrutine: { label: "Foretaksrutine", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  oppdragsrutine: { label: "Oppdragsrutine", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
};
const STATUS_MAP = {
  aktiv: { label: "Aktiv", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  utkast: { label: "Utkast", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};

const emptyRoutine = () => ({ title: "", type: "foretaksrutine", version: "1.0", status: "utkast", content: "", points: [], frequency: "arlig", applies_to: "alle" });

function PointsEditor({ points, setPoints }) {
  const upd = (i, k, v) => setPoints(points.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const add = () => setPoints([...points, { title: "", description: "", due_hint: "", responsible_hint: "" }]);
  const remove = (i) => setPoints(points.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {points.map((p, i) => (
        <div key={i} className="rounded-lg border border-border bg-secondary/40 p-3" data-testid={`routine-point-${i}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <Input value={p.title} onChange={(e) => upd(i, "title", e.target.value)} placeholder="Punkt / oppgave" className="bg-secondary border-border h-9" data-testid={`routine-point-title-${i}`} />
              <Textarea value={p.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Beskrivelse" className="bg-secondary border-border min-h-[52px] text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <Input value={p.due_hint} onChange={(e) => upd(i, "due_hint", e.target.value)} placeholder="Frist / frekvens" className="bg-secondary border-border h-9" />
                <Input value={p.responsible_hint} onChange={(e) => upd(i, "responsible_hint", e.target.value)} placeholder="Ansvarlig" className="bg-secondary border-border h-9" />
              </div>
            </div>
            <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive p-1 mt-1" data-testid={`routine-point-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="secondary" onClick={add} data-testid="routine-add-point"><Plus className="w-4 h-4 mr-1" /> Legg til punkt</Button>
    </div>
  );
}

function RoutineDialog({ open, onClose, routine, canManage, onSaved, onDeleted }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(emptyRoutine());
  const [aiLoading, setAiLoading] = useState(false);
  const [cats, setCats] = useState([]);
  const isNew = !routine?.id;

  useEffect(() => {
    if (open) api.get("/product-meta").then((r) => setCats(r.data.categories || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) {
      setF(routine?.id ? { ...routine, points: (routine.points || []).map((p) => ({ ...p })) } : emptyRoutine());
      setEdit(!routine?.id);
    }
  }, [open, routine]);

  const save = async () => {
    try {
      if (isNew) {
        await api.post("/routines", f);
        toast.success("Rutine opprettet");
      } else {
        await api.put(`/routines/${routine.id}`, f);
        toast.success("Rutine lagret");
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async () => {
    if (!window.confirm("Slette denne rutinen?")) return;
    try {
      await api.delete(`/routines/${routine.id}`);
      toast.success("Rutine slettet");
      onDeleted();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  const markRead = async () => {
    try {
      await api.post(`/routines/${routine.id}/read`);
      toast.success("Markert som lest");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  const aiSuggest = async () => {
    if (isNew) { toast.error("Lagre rutinen først, så kan AI foreslå punkter."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/routines/${routine.id}/ai-suggest`);
      const suggested = data.points || [];
      const merge = f.points.length > 0 ? window.confirm("Erstatte eksisterende punkter med AI-forslaget? Trykk Avbryt for å legge til på slutten.") : true;
      setF((prev) => ({ ...prev, points: merge ? suggested : [...prev.points, ...suggested] }));
      setEdit(true);
      toast.success(`AI foreslo ${suggested.length} punkter`);
    } catch (e) { toast.error(apiError(e)); }
    setAiLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="routine-detail">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isNew ? "Ny rutine" : f.title}</DialogTitle>
          <DialogDescription>{edit ? "Rediger rutinens innhold og punkter." : "Rutinedetaljer, punkter og lesekvittering."}</DialogDescription>
        </DialogHeader>

        {edit ? (
          <div className="space-y-4">
            <div>
              <Label className="label-caps">Tittel</Label>
              <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="routine-title-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="label-caps">Type</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                  <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="routine-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="foretaksrutine">Foretaksrutine</SelectItem>
                    <SelectItem value="oppdragsrutine">Oppdragsrutine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps">Versjon</Label>
                <Input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="routine-version-input" />
              </div>
              <div>
                <Label className="label-caps">Status</Label>
                <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                  <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="routine-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="aktiv">Aktiv</SelectItem>
                    <SelectItem value="utkast">Utkast</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-caps">Innhold / beskrivelse</Label>
              <Textarea value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className="mt-1 bg-secondary border-border min-h-[100px]" data-testid="routine-content-input" />
            </div>
            {f.type === "oppdragsrutine" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps">Frekvens</Label>
                  <Select value={f.frequency || "arlig"} onValueChange={(v) => setF({ ...f, frequency: v })}>
                    <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="routine-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="manedlig">Månedlig</SelectItem>
                      <SelectItem value="arlig">Årlig</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-caps">Gjelder (kobling)</Label>
                  <Select value={f.applies_to || "alle"} onValueChange={(v) => setF({ ...f, applies_to: v })}>
                    <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="routine-applies-to-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border max-h-60">
                      <SelectItem value="alle">Alle aktive kunder</SelectItem>
                      {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">Kunder får rutinen ved matchende produkt (f.eks. lønn, fakturering, årsregnskap), eller alle aktive kunder.</p>
                </div>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="label-caps">Punkter / oppgaver</Label>
                <Button size="sm" variant="secondary" onClick={aiSuggest} disabled={aiLoading} data-testid="routine-ai-suggest">
                  <Sparkles className="w-4 h-4 mr-1" /> {aiLoading ? "Foreslår…" : "Foreslå med AI"}
                </Button>
              </div>
              <PointsEditor points={f.points} setPoints={(pts) => setF({ ...f, points: pts })} />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge map={TYPE_MAP} value={f.type} />
              <StatusBadge map={STATUS_MAP} value={f.status} />
              <span className="text-xs text-muted-foreground">v{f.version}</span>
              {f.read_current ? (
                <span className="text-xs text-emerald-400">✓ Lest {fmtDate(f.read_at)}</span>
              ) : f.read_at ? (
                <span className="text-xs text-amber-400">Ny versjon – lest {fmtDate(f.read_at)} (v{f.read_version})</span>
              ) : (
                <span className="text-xs text-muted-foreground">Ikke lest</span>
              )}
            </div>
            {f.content && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{f.content}</p>}
            {f.type === "oppdragsrutine" && (
              <p className="text-xs text-muted-foreground" data-testid="routine-view-meta">
                Frekvens: <span className="text-foreground">{f.frequency === "manedlig" ? "Månedlig" : "Årlig"}</span> · Gjelder: <span className="text-foreground">{f.applies_to === "alle" || !f.applies_to ? "Alle aktive kunder" : f.applies_to}</span>
              </p>
            )}
            <div>
              <p className="label-caps mb-2 flex items-center gap-1"><ListChecks className="w-4 h-4" /> Punkter ({(f.points || []).length})</p>
              <div className="space-y-2">
                {(f.points || []).map((p, i) => (
                  <div key={p.id || i} className="rounded-lg border border-border bg-secondary/40 p-3" data-testid={`routine-view-point-${i}`}>
                    <p className="text-sm font-medium">{p.title}</p>
                    {p.description && <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>}
                    <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-muted-foreground">
                      {p.due_hint && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {p.due_hint}</span>}
                      {p.responsible_hint && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {p.responsible_hint}</span>}
                    </div>
                  </div>
                ))}
                {(f.points || []).length === 0 && <p className="text-sm text-muted-foreground">Ingen punkter definert ennå.</p>}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {edit ? (
            <>
              {!isNew && <Button variant="ghost" onClick={() => setEdit(false)}>Avbryt</Button>}
              <Button onClick={save} data-testid="routine-save" className="bg-white text-black hover:bg-white/90"><Check className="w-4 h-4 mr-1" /> Lagre</Button>
            </>
          ) : (
            <>
              {canManage && <Button variant="destructive" onClick={del} data-testid="routine-delete"><Trash2 className="w-4 h-4 mr-1" /> Slett</Button>}
              {canManage && <Button variant="secondary" onClick={() => setEdit(true)} data-testid="routine-edit"><Pencil className="w-4 h-4 mr-1" /> Rediger</Button>}
              <Button onClick={markRead} data-testid="routine-mark-read" className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" /> Marker som lest</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Kvalitetskontroll() {
  const { user } = useAuth();
  const canManage = user && ["admin", "management"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState("alle");
  const [selected, setSelected] = useState(undefined); // undefined=closed, null=new, obj=view

  const load = useCallback(() => {
    const q = typeFilter !== "alle" ? `?type=${typeFilter}` : "";
    api.get(`/routines${q}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [typeFilter]);
  useEffect(() => { load(); }, [load]);

  const openRoutine = async (id) => {
    try {
      const { data } = await api.get(`/routines/${id}`);
      setSelected(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Page>
      <PageHeader title="Kvalitetskontroll" subtitle="Rutiner, punkter og lesekvittering" testid="kvalitet-header">
        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44 bg-secondary border-border" data-testid="routine-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="alle">Alle typer</SelectItem>
              <SelectItem value="foretaksrutine">Foretaksrutine</SelectItem>
              <SelectItem value="oppdragsrutine">Oppdragsrutine</SelectItem>
            </SelectContent>
          </Select>
          {canManage && <Button onClick={() => setSelected(null)} data-testid="new-routine-btn" className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Ny rutine</Button>}
        </div>
      </PageHeader>

      {rows.length === 0 ? (
        <Empty icon={ClipboardCheck} title="Ingen rutiner" hint="Rutiner vises her." />
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 label-caps">Rutine</th>
                <th className="px-4 py-3 label-caps">Type</th>
                <th className="px-4 py-3 label-caps text-center">Punkter</th>
                <th className="px-4 py-3 label-caps">Versjon</th>
                <th className="px-4 py-3 label-caps">Status</th>
                <th className="px-4 py-3 label-caps">Lest</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => openRoutine(r.id)} className="border-b border-border/50 hover:bg-secondary/40 cursor-pointer" data-testid={`routine-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="px-4 py-3"><StatusBadge map={TYPE_MAP} value={r.type} /></td>
                  <td className="px-4 py-3 text-center text-muted-foreground">{r.points_count}</td>
                  <td className="px-4 py-3 text-muted-foreground">v{r.version}</td>
                  <td className="px-4 py-3"><StatusBadge map={STATUS_MAP} value={r.status} /></td>
                  <td className="px-4 py-3">
                    {r.read_current ? (
                      <span className="text-xs text-emerald-400">✓ {fmtDate(r.read_at)}</span>
                    ) : r.read_at ? (
                      <span className="text-xs text-amber-400">Ny versjon</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Ikke lest</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RoutineDialog
        open={selected !== undefined}
        routine={selected}
        canManage={canManage}
        onClose={() => setSelected(undefined)}
        onSaved={load}
        onDeleted={load}
      />
    </Page>
  );
}
